import { FunctionalComponent, h } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

interface RevealProps {
	in: boolean;
	onShow?: () => void;
	onHide?: () => void;
	duration: number;
}

type X = keyof RevealProps;
const nonNativeProps: X[] = ['in', 'onShow', 'duration', 'onHide'];

export const Reveal: FunctionalComponent<RevealProps & HTMLAttributes<HTMLDivElement>> = (props) => {
	const ref = useRef<HTMLDivElement>(null);
	const [isHidden, setHidden] = useState(!props.in);
	const htmlProps = Object.fromEntries(
		Object.entries(props).filter(([key]) => {
			return !(nonNativeProps as string[]).includes(key);
		}),
	);
	const show = useCallback(() => {
		if (!ref.current) {
			return;
		}
		setHidden(false);
		const animation = ref.current.animate(
			{
				transform: ['scaleY(0) translateY(-40%)', 'scaleY(1) translateY(0)'],
				opacity: [0, 0, 1],
				offset: [0, 0.5, 1],
			},
			{
				duration: props.duration,
				fill: 'forwards',
				easing: 'cubic-bezier(0.0, 0, 0.2, 1)',
			},
		);
		animation.onfinish = () => {
			if (props.onShow) {
				props.onShow();
			}
		};
	}, []);

	const hide = useCallback(() => {
		if (!ref.current) {
			return;
		}
		if (isHidden) {
			return;
		}
		const animation = ref.current.animate(
			{
				transform: ['scaleY(1) translateY(0%)', 'scaleY(0) translateY(-40%)'],
				opacity: [1, 0.8, 0],
				offset: [0, 0.5, 1],
			},
			{
				duration: props.duration,
				fill: 'forwards',
				easing: 'cubic-bezier(0.4, 0, 1, 1)',
			},
		);
		animation.onfinish = () => {
			if (props.onHide) {
				props.onHide();
			}
			setHidden(true);
		};
	}, [isHidden]);

	useEffect(() => {
		if (props.in) {
			show();
		} else {
			hide();
		}
	}, [ref, props.in]);

	return (
		<div ref={ref} style={{ transformOrigin: 'top' }} hidden={isHidden} {...htmlProps}>
			{props.children}
		</div>
	);
};
